import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { formatISO, parseISO } from 'date-fns';
import { BehaviorSubject, firstValueFrom, map, Observable, of, Subject } from 'rxjs';
import { LoginResponse } from '../enums/login.response.enum';
import { Net2BeepleMethodsEnum } from '../enums/net2beeple-methods.enum';
import { StorageKeysEnum } from '../enums/storage.keys.enum';
import { HttpService } from '../http/http.service';
import { ServerInfoService } from '../server/serverinfo.service';
import { SignInReplyModel } from './models/signin-reply.model';
import { AccessToken, PreAuthToken, RefreshToken, Token } from './models/token.model';
import packageInfo from '../../../../package.json';
import { CompanyService } from '../company/company.service';
import { OUService } from '../../modules/admin/ou/ou.service';
import { UserService } from '../user/user.service';
import { environment } from '../../../environments/environment';
import { AuthUtils } from './auth.utils';

@Injectable({providedIn: 'root'})
export class AuthService {

    private companyService = inject(CompanyService);
    private httpService = inject(HttpService);
    private ouService = inject(OUService);
    private router: Router = inject(Router);
    private serverInfoService = inject(ServerInfoService);
    private userService = inject(UserService);
    
    uuid: string;
    scopes = environment.scopes;
    serverInfo = this.serverInfoService.getServerInfoObservable();

    refreshing = false;
    tokenRefreshedSource: Subject<void> = new Subject();
    tokenRefreshed$: Observable<void> = this.tokenRefreshedSource.asObservable();
    loginEvent$ = new BehaviorSubject(false);
    deleteGenericMessages = new Subject<void>();

    constructor() {
        if (environment.dist) {
            this.serverInfo.subscribe((serverInfo) => {
                if (serverInfo) {
                    this.scopes = [];
                    this.scopes.push(...serverInfo.scopes, serverInfo.applicationUri, serverInfo.environmentUri);
                } else {
                    this.scopes.push(...environment.scopes, environment.applicationUri, environment.environmentUri);
                }
            });
        }
        this.uuid = localStorage.getItem(StorageKeysEnum.uuid);
        if (!this.uuid) {
            this.uuid = AuthUtils.createUUID();
            localStorage.setItem(StorageKeysEnum.uuid, this.uuid);
        }
    }

    get accessToken(): string {
        return localStorage.getItem(StorageKeysEnum.accessToken) ?? '';
    }

    get accessTokenExpirationTime(): Date {
        const str = localStorage.getItem(StorageKeysEnum.accessTokenExpirationTime);
        return str ? parseISO(str) : undefined;
    }

    get preauthorizeToken(): string {
        return localStorage.getItem(StorageKeysEnum.preauthorizeToken) ?? '';
    }

    get preauthorizeTokenExpirationTime(): Date {
        const str = localStorage.getItem(StorageKeysEnum.preauthorizeTokenExpirationTime);
        return str ? parseISO(str) : undefined;
    }

    get refreshToken(): string {
        return localStorage.getItem(StorageKeysEnum.refreshToken) ?? '';
    }

    get refreshTokenExpirationTime(): Date {
        const str = localStorage.getItem(StorageKeysEnum.refreshTokenExpirationTime);
        return str ? parseISO(str) : undefined;
    }

    set accessToken(token: string) {
        localStorage.setItem(StorageKeysEnum.accessToken, token);
    }

    set accessTokenExpirationTimeStr(tokenExpirationTime: string) {
        localStorage.setItem(StorageKeysEnum.accessTokenExpirationTime, tokenExpirationTime);
    }

    set preauthorizeToken(token: string) {
        localStorage.setItem(StorageKeysEnum.preauthorizeToken, token);
    }

    set preauthorizeTokenExpirationTimeStr(tokenExpirationTime: string) {
        localStorage.setItem(StorageKeysEnum.preauthorizeTokenExpirationTime, tokenExpirationTime);
    }

    set refreshToken(token: string) {
        localStorage.setItem(StorageKeysEnum.refreshToken, token);
    }

    set refreshTokenExpirationTimeStr(tokenExpirationTime: string) {
        localStorage.setItem(StorageKeysEnum.refreshTokenExpirationTime, tokenExpirationTime);
    }

    private set accessTokenModel(value: AccessToken) {
        this.accessToken = value.accessToken;
        this.accessTokenExpirationTimeStr = value.accessTokenExpirationTime;
    }

    private set preauthorizeTokenModel(value: PreAuthToken) {
        this.preauthorizeToken = value.token;
        this.preauthorizeTokenExpirationTimeStr = value.expirationTime;
    }

    private set refreshTokenModel(value: RefreshToken) {
        this.refreshToken = value.refreshToken;
        this.refreshTokenExpirationTimeStr = value.refreshTokenExpirationTime;
    }

    preauthTokenIsValid() {
        return AuthUtils.isTokenValid(this.preauthorizeToken, this.preauthorizeTokenExpirationTime);
    }

    accessTokenIsValid() {
        return AuthUtils.isTokenValid(this.accessToken, this.accessTokenExpirationTime);
    }

    refreshTokenIsValid() {
        return AuthUtils.isTokenValid(this.refreshToken, this.refreshTokenExpirationTime);
    }

    /**
     * Preauthorize
     *
     * @returns token to be used in signIn
     */
     preauthorize(): Observable<PreAuthToken> {
        return this.httpService.authPost<PreAuthToken>(Net2BeepleMethodsEnum.preauthorize, {
            uuid: this.uuid,
            scope: this.scopes,
        });
    }

    /**
     * Exchange preauthorization token for the access token
     *
     * @param preAuthToken
     * @returns access token to be used in http requests
     */
    exchangeToken(preAuthToken: string): Observable<Token> {
        return this.httpService.authPost<Token>(Net2BeepleMethodsEnum.exchangeToken, {
            token: preAuthToken,
            scope: this.scopes,
            uuid: this.uuid,
        });
    }

    /**
     * Refresh access token
     */
    doRefreshToken(): Observable<void> {
        if (this.refreshing) {
            return new Observable<void>((observer) => {
                this.tokenRefreshed$.subscribe(() => {
                    observer.next();
                    observer.complete();
                });
            });
        } else {
            this.refreshing = true;

            return this.httpService.authPost<AccessToken>(Net2BeepleMethodsEnum.refreshToken, {
                token: this.refreshToken,
                uuid: this.uuid,
            }).pipe(
                map((token) => {
                    this.accessTokenModel = token;
                    this.tokenRefreshedSource.next();
                    this.refreshing = false;
                })
            );
        }
    }

    /**
     * Get new preauthorize token or the preauthorization token in the storage
     *
     * @returns new or existing preauthorize token
     */
    getOrRefreshPreauthorizeToken(): Observable<PreAuthToken> {
        if (this.preauthTokenIsValid()) {
            return of({
                token: this.preauthorizeToken,
                expirationTime: formatISO(this.preauthorizeTokenExpirationTime),
            });
        } else {
            return this.preauthorize().pipe(
                map((token) => {
                    if (!token) {
                        throw new Error('NO TOKEN RETURNED');
                    }
                    return token;
                }),
            );
        }
    }

    /**
     * Execute all the login flow (preauthorize, signIn, exchangeToken)
     *
     * @param credentials
     */
    login(credentials: { user: string; password: string }): Promise<any> {
        return new Promise(async (resolve, reject) => {
            try {
                const preToken = await this.getOrRefreshPreauthorizeToken().toPromise();
                if (preToken) {
                    this.preauthorizeTokenModel = preToken;

                    const signInReply: SignInReplyModel = await this.signIn(credentials, preToken.token).toPromise();
                    if (signInReply.signedIn) {
                        this.saveUser(signInReply);
                        const token = await firstValueFrom(this.exchangeToken(preToken.token));
                        if (token) {
                            this.accessTokenModel = token;
                            this.refreshTokenModel = token;
                            localStorage.removeItem(StorageKeysEnum.preauthorizeToken);
                            localStorage.removeItem(StorageKeysEnum.preauthorizeTokenExpirationTime);
                            resolve(LoginResponse.LOGIN_SUCCESS);
                        }
                    } else {
                        resolve(LoginResponse.LOGIN_ERROR);
                    }
                }
            } catch (exception) {
                console.error(exception);
                reject(!!exception && !!exception.error ? exception.error : exception);
            }
        });
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { user: string; password: string }, token: string): Observable<SignInReplyModel> {
        const userCredentials = {
            user: credentials.user,
            password: credentials.password,
            scope: this.scopes,
            preauthorizationToken: token,
            deviceApplicationUri: environment.applicationId,
            device: {
                appVersion: require('../../../../package.json').version,
                deviceType: 'web',
                deviceUUID: this.uuid,
                deviceInfo: {
                    platformVersion: AuthUtils.getBrowserVersion()
                }
            }
        };
        return this.httpService.post<SignInReplyModel>(Net2BeepleMethodsEnum.signIn, userCredentials);
    }

    /**
     * Returns if the tokens are valid
     */
    async isLoggedIn(): Promise<LoginResponse> {
        const serverInfo = await this.serverInfoService.fetchServerInfo();
        if (serverInfo) {
            if (!this.accessTokenIsValid()) {
                if (this.refreshTokenIsValid()) {
                    try {
                        await firstValueFrom(this.doRefreshToken());
                        if (!this.loginEvent$.value) {
                            this.loginEvent$.next(true);
                        }
                        return LoginResponse.LOGIN_SUCCESS;
                    } catch (err) {
                        console.error(err);
                        this.deleteTokens();
                        this.refreshing = false;
                        this.loginEvent$.next(false);
                        return LoginResponse.SERVER_ERROR;
                    }
                } else {
                    this.deleteTokens();
                    this.refreshing = false;
                    this.loginEvent$.next(false);
                    return LoginResponse.LOGOUT_SUCCESS;
                }
            } else {
                if (!this.loginEvent$.value) {
                    this.loginEvent$.next(true);
                }
                return LoginResponse.LOGIN_SUCCESS;
            }
        } else {
            console.error('No ha sido posible obtener la información del servidor');
            this.deleteTokens();
            this.refreshing = false;
            this.loginEvent$.next(false);
            return LoginResponse.SERVER_ERROR;
        }
    }

    /**
     * Logout
     */
    async logout(redirect?: boolean) {
        try {
            await firstValueFrom(this.changeUserStatus());
        } catch (error) {
            console.error(error);
        }
        this.deleteTokens();
        this.userService.delete();
        this.companyService.delete();
        this.ouService.setRole(undefined);
        this.deleteGenericMessages.next();
        if (redirect) {
            this.router.navigate(['sign-in']);
        }
        this.loginEvent$.next(false);
        return true;
    }

    /**
     * Delete tokens from local storage
     */
    deleteTokens() {
        localStorage.removeItem(StorageKeysEnum.accessToken);
        localStorage.removeItem(StorageKeysEnum.accessTokenExpirationTime);
        localStorage.removeItem(StorageKeysEnum.refreshToken);
        localStorage.removeItem(StorageKeysEnum.refreshTokenExpirationTime);
    }

    private saveUser(signInReply: SignInReplyModel): void {
        this.userService.user = signInReply.companyUser;
        this.companyService.company = signInReply.company;
    }

    private changeUserStatus() {
        return this.httpService.post(Net2BeepleMethodsEnum.changeUserStatus, {
            deviceApplicationUri: environment.applicationId,
            sessionClosed: true,
            deviceUUID: this.uuid,
            appVersion: packageInfo.version
        });
    }
}
